import { motion } from "framer-motion";
import { RoundType } from "types/enums";
import type { RoundConfiguration } from "Models";
import { customStyles } from "utils/ui/uiHelpers";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import { useFacilityRoundConfigurations } from "modules/round-configuration/roundConfigurationHooks";
import Select from "react-select";
import Row from "components/ui/layout/Row";
import GearIcon from "components/svg/GearIcon";
import { useFormContext } from "react-hook-form";
import Tooltip from "@/components/common/Tooltip";

const RoundConfigurationSelect: React.FC<RoundConfigurationSelectProps> = (
  props,
) => {
  const { setValue } = useFormContext();
  const currentFacilityId = useGetCurrentLocalFacilityId();

  const [facilityRoundConfigs] =
    useFacilityRoundConfigurations(currentFacilityId);

  return (
    <>
      <Row cssClasses="flex items-center flex-row">
        <div className="poppins text-sm block text-black dark:text-gray-200">
          <GearIcon class="size-4 mr-1" /> Quick Start: Select a Saved Round
        </div>
        <Tooltip
          key={`round-configuration-select-tippy`}
          className="z-[9999] w-full max-w-[800px] bg-gray-50 dark:bg-zinc-900 border-gray-200 rounded-lg"
          arrow={true}
          placement="auto"
          delay={[750, 0]}
          content={
            <div className="p-2 flex flex-col w-full max-h-[500px] overflow-y-auto text-xs dark:text-gray-50">
              Selecting a saved round will populate the fields in the form below
              with the selected saved round settings. You can also create a new
              saved round by going to Clubhouse Admin and click on Round
              Presets.
            </div>
          }
        >
          <div className="inline items-center cursor-pointer border-b border-dashed border-blue-500 text-xs ml-4 dark:text-gray-50">
            What&apos;s this?
          </div>
        </Tooltip>
      </Row>

      <motion.div
        className="flex-row mt-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Select
          isSearchable={true}
          isClearable={true}
          closeMenuOnSelect={true}
          options={
            props.selectedScoringType
              ? facilityRoundConfigs.filter(
                  (config) => config.roundType === props.selectedScoringType,
                )
              : facilityRoundConfigs
          }
          styles={customStyles}
          getOptionLabel={(config: RoundConfiguration) =>
            config.name || "Defaults"
          }
          getOptionValue={(config: RoundConfiguration) => config.id}
          onChange={(option) => {
            if (!option) {
              return;
            }
            const config = option as RoundConfiguration;
            props.onChange(config);

            if (props.applyConfigurationToForm) {
              props.applyConfigurationToForm(config);
              return;
            }

            try {
              Object.entries(config).forEach(([key, value]) => {
                if (key === "noPuttMode") {
                  setValue("roundInfo.strokeSettings.noPuttMode", value);
                  setValue("roundInfo.scoring.noPuttMode", value);
                  setValue("roundInfo.noPuttMode", value);
                } else {
                  setValue(key, value);
                }
              });
            } catch (err) {
              console.error(err);
            }
          }}
          placeholder={"Select a Saved Round Setting"}
        />
        <Row cssClasses="pt-2 text-gray-400 dark:text-gray-200 text-xs mb-4 pb-2">
          Select an option to fill the fields below with the saved round
          settings.
        </Row>
      </motion.div>
    </>
  );
};
export default RoundConfigurationSelect;

type RoundConfigurationSelectProps = {
  onChange: React.Dispatch<
    React.SetStateAction<RoundConfiguration | undefined>
  >;
  selectedScoringType?: RoundType;
  /**
   * When set (e.g. create-competition), called instead of the default
   * `roundInfo` / top-level setValue loop so the parent can map into its own
   * form shape (e.g. `round1Settings.*`). Use the parent `useFormContext` if
   * you need setValue.
   */
  applyConfigurationToForm?: (config: RoundConfiguration) => void;
};
